import { useCallback, useEffect, useRef, useState } from "react";
import { TurfTalk } from "@/api/client";
import { buildDiscussMessage } from "@/lib/news";
import { createStorySharer, SHARE_RESULT } from "@/lib/roomShare";

// Owns all "Debate this" share state at GAME scope. The hook lives in the game
// page, which survives Room↔Overview tab remounts — so the share controller,
// idempotency keys, and in-flight set persist across tab switches:
//   - re-entering the Room tab never auto-resends (sending happens only on an
//     explicit debateStory call, never in a mount effect),
//   - a failed/uncertain send keeps its key for a manual retry,
//   - completions are GENERATION-scoped, so a late result from a previous game
//     (or an out-of-order official-room resolution) can never mark the current
//     game's story, and a new game's story is only ever sent through its own
//     freshly-resolved official room.
export function useRoomHandoff(gameId) {
  const [storyStatus, setStoryStatus] = useState({}); // id -> "pending" | "shared" | "failed"
  const [roomRefresh, setRoomRefresh] = useState(0);   // nudges the embedded chat after a confirmed share
  const statusRef = useRef({});
  const genRef = useRef(0);
  const mountedRef = useRef(false);
  const ctxMapRef = useRef(new Map());      // gameId -> { roomId, sharer }
  const roomPromiseRef = useRef(new Map());  // gameId -> in-flight official-room resolution

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const setStatus = useCallback((updater) => {
    setStoryStatus((m) => { const next = typeof updater === "function" ? updater(m) : updater; statusRef.current = next; return next; });
  }, []);

  // New game → new generation; drop the previous game's per-story status.
  useEffect(() => { genRef.current += 1; setStatus({}); }, [gameId, setStatus]);

  // Resolve (once per game) the official room + its persistent share controller.
  // A FAILED lookup is evicted so a later manual Retry re-fetches and can
  // recover (a rejected promise is never cached forever). A real roomId is
  // validated before any controller/POST is created.
  const resolveCtx = useCallback((gid) => {
    let p = roomPromiseRef.current.get(gid);
    if (!p) {
      p = (async () => {
        const r = await TurfTalk.officialRoom(gid);
        const roomId = (r && r.room && r.room.id) || (r && r.id);
        if (!roomId) throw new Error("official room unavailable");
        const ctx = { roomId, sharer: createStorySharer({ postFn: (payload) => TurfTalk.postRoomMessage(roomId, payload), buildBody: buildDiscussMessage }) };
        ctxMapRef.current.set(gid, ctx);
        return ctx;
      })();
      // Evict a failed lookup so Retry re-fetches; keep successful ones cached.
      p.catch(() => { if (roomPromiseRef.current.get(gid) === p) roomPromiseRef.current.delete(gid); });
      roomPromiseRef.current.set(gid, p);
    }
    return p;
  }, []);

  const shareToRoom = useCallback(async (item) => {
    const gen = genRef.current;
    const gid = gameId;
    try {
      const ctx = await resolveCtx(gid);
      if (genRef.current !== gen || !mountedRef.current) return; // game changed / unmounted
      const res = await ctx.sharer.share(item);
      if (genRef.current !== gen || !mountedRef.current) return; // stale completion → ignore
      if (res.status === SHARE_RESULT.SHARED) { setStatus((m) => ({ ...m, [item.id]: "shared" })); setRoomRefresh((n) => n + 1); }
      else if (res.status === SHARE_RESULT.FAILED || res.status === SHARE_RESULT.INVALID) setStatus((m) => ({ ...m, [item.id]: "failed" }));
      // A duplicate click subscribes to the SAME in-flight promise above, so
      // the current generation observes the eventual shared/failed outcome
      // without a second POST.
    } catch {
      if (genRef.current === gen && mountedRef.current) setStatus((m) => ({ ...m, [item.id]: "failed" }));
    }
  }, [gameId, resolveCtx, setStatus]);

  // Explicit user action (initial "Debate this" OR a manual retry of a failed
  // story). Ignores clicks on a story already pending/shared so rapid clicks
  // can't duplicate the send.
  const debateStory = useCallback((item) => {
    if (!item || !item.id) return;
    const cur = statusRef.current[item.id];
    if (cur === "pending" || cur === "shared") return;
    setStatus((m) => ({ ...m, [item.id]: "pending" }));
    shareToRoom(item);
  }, [setStatus, shareToRoom]);

  return { storyStatus, roomRefresh, debateStory };
}
